import { LeaderboardOptionTypeEnum } from '@/enums/powerMarkets/LeaderboardEnums.ts';
import { getTooltipTableRow, getTooltipWrapper } from '@/helpers/charts/chartSettings.ts';
import {
    ensureLeaderboardBenchmarkPoolReference,
    isAssetPoolLeaderboard,
    type IResolveBenchmarkPoolOptions,
} from '@/helpers/powerMarkets/benchmarkPoolHelpers.ts';
import type { IDateRangeCompositeNumbers } from '@/interfaces/general/IDateRange.ts';
import type { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import type { IDateRangeByString } from '@/interfaces/general/IDateRangeByString.ts';
import { BenchmarkPoolScopeTypeEnum, IBenchmarkPoolScope, ILeaderboardBenchmarkPoolReference } from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';
import { ILeaderboardGroup } from '@/interfaces/powerMarkets/leaderboard/ILeaderboardGroup.ts';
import { ILeaderboardTablePayload } from '@/interfaces/powerMarkets/payloads/ILeaderboardTablePayload.ts';
import {
    createDefaultLeaderboardDateModel,
    type ILeaderboardUserOptions,
    ILeaderboardModalOptions,
} from '@/interfaces/powerMarkets/settings/pages/ILeaderboardSettings.ts';
import { ALL_BENCHMARK_POOL_FUELS } from '@/helpers/powerMarkets/benchmarkPoolDefinitionHelpers.ts';
import type { ILeaderboardCsvDownloadPayload } from '@/interfaces/powerMarkets/payloads/ILeaderboardCsvDownloadPayload.ts';
import { cloneDeep } from '@/utils/global.ts';
import { dateFormatToAnyFormat, getDateObjectFromDatePicker } from '@/utils/dateUtils.ts';
import type { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import type { TooltipFormatterContextObject } from 'highcharts';

type LegacyLeaderboardUserOptions = ILeaderboardUserOptions & { selectedDateRange?: IDateRangeByString };

export function getDateModelFromLeaderboardUserOptions (userOptions: LegacyLeaderboardUserOptions): IAdvancedDatePickerModel {
    if (userOptions.selectedDateModel) return userOptions.selectedDateModel;
    if (userOptions.selectedDateRange?.from != null && userOptions.selectedDateRange?.to != null) {
        return { dateType: 'fixed', dateRange: userOptions.selectedDateRange };
    }
    return createDefaultLeaderboardDateModel();
}

/** Returns the fuels array for the given leaderboard type (Plant → fuels, Owner/Optimiser → ownerFuels). */
export function getFuelsForLeaderboardType (userOptions: ILeaderboardUserOptions): string[] {
    const arr = userOptions.leaderboardType === LeaderboardOptionTypeEnum.Plant
        ? userOptions.fuels
        : userOptions.ownerFuels;
    return arr ?? [];
}

export function leaderboardTooltipFormatter (this: TooltipFormatterContextObject): string {
    let rows = '';
    this.points?.forEach((point: TooltipFormatterContextObject) => {
        const y = (point.y || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        rows += getTooltipTableRow({
            label: point.series.name,
            value: `£${y}`,
            color: point.color,
        });
    });

    return getTooltipWrapper({
        content: rows,
        contentType: 'table',
        header: this.x?.toString() || '',
    });
}

export interface IBuildLeaderboardTablePayloadParams {
    type: LeaderboardOptionTypeEnum;
    userOptions: ILeaderboardUserOptions;
    date?: IDateRangeCompositeNumbers;
    benchmarkPoolReference?: ILeaderboardBenchmarkPoolReference | null;
    benchmarkPoolScope?: IBenchmarkPoolScope;
}

export function buildLeaderboardTablePayload (params: IBuildLeaderboardTablePayloadParams): ILeaderboardTablePayload {
    const payload: ILeaderboardTablePayload = {
        type: params.type,
        userOptions: params.userOptions,
        date: params.date,
    };

    if (params.benchmarkPoolReference) {
        payload.benchmarkPool = {
            poolId: params.benchmarkPoolReference.poolId,
            revisionId: params.benchmarkPoolReference.revisionId,
        };
        if (params.benchmarkPoolScope?.type === BenchmarkPoolScopeTypeEnum.Category) {
            payload.benchmarkPoolScope = cloneDeep(params.benchmarkPoolScope);
        }
    }

    return payload;
}

export async function buildLeaderboardTablePayloadForGroup (
    group: ILeaderboardGroup,
    userOptions: ILeaderboardUserOptions,
    date?: IDateRangeCompositeNumbers,
    resolveOptions?: IResolveBenchmarkPoolOptions,
): Promise<ILeaderboardTablePayload> {
    // Snapshot options and resolve relative dates once, before any asynchronous resolution.
    const options = cloneDeep(userOptions);
    const benchmarkPoolScope = isAssetPoolLeaderboard(group) && group.benchmarkPoolScope ? cloneDeep(group.benchmarkPoolScope) : undefined;
    let effectiveDate = date ? { ...date } : undefined;
    if (!effectiveDate && isAssetPoolLeaderboard(group)) {
        effectiveDate = dateFormatToAnyFormat<IDateRangeCompositeNumbers>(
            getDateObjectFromDatePicker(getDateModelFromLeaderboardUserOptions(options)),
            'IDateRangeCompositeNumbers',
        );
    }
    if (isAssetPoolLeaderboard(group)) {
        options.leaderboardType = LeaderboardOptionTypeEnum.Plant;
        if (!options.fuels.length) options.fuels = [...ALL_BENCHMARK_POOL_FUELS];
    }
    const type = (options.leaderboardType ?? group.userOptions.leaderboardType) as LeaderboardOptionTypeEnum;
    let benchmarkPoolReference: ILeaderboardBenchmarkPoolReference | null = null;

    if (isAssetPoolLeaderboard(group)) {
        benchmarkPoolReference = await ensureLeaderboardBenchmarkPoolReference(group, { ...resolveOptions, date: effectiveDate });
    }

    return buildLeaderboardTablePayload({
        type,
        userOptions: options,
        date: effectiveDate,
        benchmarkPoolReference,
        benchmarkPoolScope,
    });
}

/** Comparison calculations share the exact resolved population and period of the main request. */
export function buildLeaderboardComparisonPayload (
    payload: ILeaderboardTablePayload,
    comparisonOptions: ILeaderboardModalOptions,
): ILeaderboardTablePayload {
    const snapshot = cloneDeep(payload) as ILeaderboardTablePayload;
    snapshot.userOptions = { ...snapshot.userOptions, ...cloneDeep(comparisonOptions) };
    return snapshot;
}

export async function buildLeaderboardCsvPayloadForGroup (
    group: ILeaderboardGroup,
    userOptions: ILeaderboardUserOptions,
    date: IDateRangeCompositeNumbers,
    resolveOptions?: IResolveBenchmarkPoolOptions,
): Promise<ILeaderboardCsvDownloadPayload> {
    const payload = await buildLeaderboardTablePayloadForGroup(group, userOptions, date, resolveOptions);
    return {
        type: payload.type,
        userOptions: payload.userOptions,
        date: payload.date ?? { ...date },
        ...(payload.benchmarkPool ? { benchmarkPool: payload.benchmarkPool } : {}),
        ...(payload.benchmarkPoolScope ? { benchmarkPoolScope: payload.benchmarkPoolScope } : {}),
    };
}

/** Decorate only the benchmark row, leaving source data and interactive selection unchanged. */
export function highlightLeaderboardBenchmarkAsset (table: IGenericTableData, benchmarkPlantId?: string): IGenericTableData {
    if (!benchmarkPlantId) return table;
    return {
        ...table,
        data: table.data.map(row => {
            const plantCell = row.row['Plant - ID'];
            const plantId = row.alternativeHiddenString || plantCell?.cellFormatProperties?.alternativeHiddenString || plantCell?.values[0];
            if (plantId !== benchmarkPlantId) return row;
            return {
                ...row,
                boldRow: true,
                row: Object.fromEntries(Object.entries(row.row).map(([id, cell]) => [id, {
                    ...cell,
                    backgroundColour: 'var(--lcpBlueHover)',
                    textColour: 'var(--primaryFont)',
                }])),
            };
        }),
    };
}
